import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import InstructorSurveyListFeature from '../../features/InstructorDashboardFeature'
import SkipNavigation from '../../components/SkipNavigation'
import TermHeader from '../../components/TermHeader'
import { Export } from '../../features/InstructorDashboardFeature/components/Export'
import { getUserProfile } from '../../store/slices/userProfileSlice/selectors'
import { DistributionManager, TermsManager, UserManager } from '../../APIManager'
import PageHeader from '../../components/PageHeader'
import { CircularProgress } from '@mui/material'
import LoadingPageV2 from '../LoadingPageV2'

const InstructorDashboardPage: React.FC = () => {
  const [instructorName, setInstructorName] = useState('')
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const termId = searchParams.get('termId')
  const user = getUserProfile()
  const userId = user?.userId

  const {
    data: terms = [],
    isLoading: termsLoading
  } = useQuery({
    queryKey: ['terms', id ?? userId],
    staleTime: 1000,
    queryFn: async () => (await TermsManager.getAllTerms({ pageSize: 200, userId: id ?? userId })).data,
    retry: false,
    enabled: Boolean(id ?? userId)
  })

  const {
    data: instructorDistributions = [],
    isLoading: distributionsLoading
  } = useQuery({
    queryKey: ['instructorDistributions', id ?? 'self', termId],
    queryFn: async () => {
      const params = { termId }
      return id
        ? (await DistributionManager.getInstructorDistributionsById(id, params)).data
        : (await DistributionManager.getInstructorDistributions(params)).data
    },
    enabled: Boolean(termId && (id || id === undefined)),
    retry: false
  })

  const {
    data: facultyData,
    isLoading: facultyLoading
  } = useQuery({
    queryKey: ['faculty', id],
    queryFn: async () => (await UserManager.getFacultyAndStaffByIdApi(id ?? '')).data,
    enabled: Boolean(id)
  })

  useEffect(() => {
    if (facultyData) {
      setInstructorName(`${facultyData.firstName} ${facultyData.lastName}`)
    } else if (!id && user) {
      setInstructorName(user?.name ?? 'Courses')
    }
  }, [facultyData, user, id])

  if (facultyLoading || termsLoading) {
    return <LoadingPageV2 />
  }

  return (
    <Box>
      <SkipNavigation />
      <CustomHeader info={distributionsLoading ? PAGE_TITLES.INSTRUCTOR_DASHBOARD : instructorName} />
      <PageHeader
        title={instructorName}
        pageTitleAriaLabel={`Survey results for ${instructorName}`}
        controls={
          <TermHeader showSeries={false} terms={terms} termsLoading={termsLoading}>
            <Export />
          </TermHeader>
        }
        isCompactOnly
      />
      {distributionsLoading
        ? <Box display="flex" justifyContent="center" alignItems={'center'} height={400} mt={2}>
          <CircularProgress />
        </Box>
        : <InstructorSurveyListFeature list={instructorDistributions} />
}
    </Box>
  )
}

export default InstructorDashboardPage
